<template>
  <div class="glass-card p-4">
    <h3 class="font-bold mb-3 flex items-center justify-between">
      <span class="flex items-center gap-2">
        <span class="pulse-dot"></span>
        Live Activity
      </span>
      <button class="text-xs themed-text-secondary hover:underline" @click="markAllRead">
        Mark all read
      </button>
    </h3>
    
    <div class="space-y-3 max-h-64 overflow-y-auto">
      <div v-for="activity in activities" :key="activity.id" 
           class="flex gap-3 p-2 rounded-lg transition-all hover:bg-white/10 dark:hover:bg-black/10"
           :class="{ 'opacity-60': activity.read }">
        <!-- Avatar -->
        <div class="flex-shrink-0">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
            {{ activity.user.charAt(0) }}
          </div>
        </div>
        
        <!-- Content -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium">
            <span class="font-semibold">{{ activity.user }}</span>
            <span class="themed-text-secondary"> {{ activity.action }}</span>
          </p>
          <p class="text-xs themed-text-secondary truncate">{{ activity.target }}</p>
          <p class="text-xs themed-text-secondary mt-1">{{ formatTime(activity.timestamp) }}</p>
        </div>
        
        <!-- Status icon -->
        <div class="flex-shrink-0">
          <div :class="statusIconClass(activity.status)" class="w-6 h-6 rounded-full flex items-center justify-center">
            <component :is="statusIcon(activity.status)" class="w-3 h-3" />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Load more -->
    <button v-if="hasMore" class="mt-3 w-full text-center text-sm themed-text-secondary hover:underline" @click="loadMore">
      Load more activities
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const activities = ref([
  {
    id: 1,
    user: 'Yutthana',
    action: 'completed spraying on',
    target: 'Field A-3 (45 hectares)',
    status: 'success',
    timestamp: Date.now() - 2 * 60 * 1000,
    read: false
  },
  {
    id: 2,
    user: 'System',
    action: 'deployed drone',
    target: 'BY-213322010133 - TEST01',
    status: 'info',
    timestamp: Date.now() - 15 * 60 * 1000,
    read: false
  },
  {
    id: 3,
    user: 'Alert',
    action: 'maintenance required for',
    target: 'BY-213322010135 - Battery check',
    status: 'warning',
    timestamp: Date.now() - 60 * 60 * 1000,
    read: false
  },
  {
    id: 4,
    user: 'Weather API',
    action: 'rain expected in',
    target: 'Region North - 2 hours',
    status: 'warning',
    timestamp: Date.now() - 90 * 60 * 1000,
    read: true
  }
])

const hasMore = ref(true)

const statusIcon = (status) => {
  switch (status) {
    case 'success':
      return 'CheckIcon'
    case 'warning':
      return 'AlertTriangleIcon'
    case 'info':
      return 'InfoIcon'
    default:
      return 'CircleIcon'
  }
}

const statusIconClass = (status) => {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300'
    case 'warning':
      return 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300'
    case 'info':
      return 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
    default:
      return 'bg-gray-100 text-gray-600 dark:bg-gray-900 dark:text-gray-300'
  }
}

const formatTime = (timestamp) => {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return new Date(timestamp).toLocaleDateString()
}

const markAllRead = () => {
  activities.value.forEach(a => a.read = true)
}

const loadMore = () => {
  // Simulate loading more activities
  hasMore.value = false
}

// Simple icon components
const CheckIcon = { template: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>' }
const AlertTriangleIcon = { template: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>' }
const InfoIcon = { template: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>' }
const CircleIcon = { template: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke-width="2"/></svg>' }

onMounted(() => {
  // Simulate real-time updates
  setInterval(() => {
    const newActivity = {
      id: Date.now(),
      user: ['System', 'Weather API', 'Drone Fleet'][Math.floor(Math.random() * 3)],
      action: ['updated', 'detected', 'completed'][Math.floor(Math.random() * 3)],
      target: `Field ${String.fromCharCode(65 + Math.floor(Math.random() * 26))}-${Math.floor(Math.random() * 10)}`,
      status: ['success', 'info', 'warning'][Math.floor(Math.random() * 3)],
      timestamp: Date.now(),
      read: false
    }
    activities.value.unshift(newActivity)
    if (activities.value.length > 10) activities.value.pop()
  }, 30000)
})
</script>
